import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { DEFAULT_CONFIG } from '../constants/Formularios';
import taxConceptsService from '../services/taxConceptsService';
import documentTypesService from '../services/documentTypesService';

/**
 * ------------------------------------------------------------------
 * INDICE DE FUNCIONES Y ESTRUCTURA
 * ------------------------------------------------------------------
 * 1. Context Creation .................... Creación del contexto
 * 2. Custom Hook (useAppConfig) .......... Hook para usar la configuración
 * 3. Provider (AppConfigProvider) ........ Componente proveedor
 *    - State (config) .................... Estado de configuración (persiste en localStorage)
 *    - State (taxConcepts) ............... Conceptos de impuestos cargados desde API
 *    - State (taxConceptsLoading) ........ Indicador de carga de conceptos
 *    - State (documentTypes) ............. Tipos de documento cargados desde API
 *    - State (documentTypesLoading) ...... Indicador de carga de tipos de documento
 *    - updateConfig ...................... Actualizar una clave de configuración
 *    - resetConfig ....................... Restaurar configuración por defecto
 *    - getTaxConceptsByTipo .............. Filtrar conceptos por tipo (ISLR, IVA, etc.)
 *    - getTaxConceptsAsOptions ........... Formatear conceptos para componentes Select
 *    - getTaxConceptById ................. Obtener un concepto específico por ID
 *    - documentTypesOptions .............. Tipos de documento formateados para Select
 * ------------------------------------------------------------------
 */

/**
 * ------------------------------------------------------------------
 * 1. Context Creation
 * ------------------------------------------------------------------
 */
const AppConfigContext = createContext();

/**
 * ------------------------------------------------------------------
 * 2. Custom Hook (useAppConfig)
 * ------------------------------------------------------------------
 */
export function useAppConfig() {
    return useContext(AppConfigContext);
}

/**
 * ------------------------------------------------------------------
 * 3. Provider (AppConfigProvider)
 * ------------------------------------------------------------------
 */
export function AppConfigProvider({ children }) {
    const { isAuthenticated, loading: authLoading } = useAuth();

    // State (config)
    const [config, setConfig] = useState(() => {
        // Cargar configuración desde localStorage
        const stored = localStorage.getItem('appConfig');
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                console.error('Error parsing appConfig:', e);
            }
        }
        // Configuración por defecto
        return DEFAULT_CONFIG;
    });

    // State para conceptos de impuestos
    const [taxConcepts, setTaxConcepts] = useState([]);
    const [taxConceptsLoading, setTaxConceptsLoading] = useState(true);

    // State para tipos de documento
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentTypesLoading, setDocumentTypesLoading] = useState(true);

    // Guardar configuración en localStorage cuando cambie
    useEffect(() => {
        localStorage.setItem('appConfig', JSON.stringify(config));
    }, [config]);

    // Cargar conceptos de impuestos al iniciar
    useEffect(() => {
        if (authLoading || !isAuthenticated) {
            setTaxConcepts([]);
            setTaxConceptsLoading(false);
            return;
        }

        const loadTaxConcepts = async () => {
            setTaxConceptsLoading(true);
            try {
                const concepts = await taxConceptsService.getAll();
                setTaxConcepts(concepts);
            } catch (error) {
                console.error('Error loading tax concepts:', error);
                // No bloquear la app si falla la carga de conceptos
                setTaxConcepts([]);
            } finally {
                setTaxConceptsLoading(false);
            }
        };

        loadTaxConcepts();
    }, [isAuthenticated, authLoading]);

    // Cargar tipos de documento al iniciar
    useEffect(() => {
        if (authLoading || !isAuthenticated) {
            setDocumentTypes([]);
            setDocumentTypesLoading(false);
            return;
        }

        const loadDocumentTypes = async () => {
            setDocumentTypesLoading(true);
            try {
                const types = await documentTypesService.getAll();
                setDocumentTypes(types);
            } catch (error) {
                console.error('Error loading document types:', error);
                // No bloquear la app si falla la carga de tipos
                setDocumentTypes([]);
            } finally {
                setDocumentTypesLoading(false);
            }
        };

        loadDocumentTypes();
    }, [isAuthenticated, authLoading]);

    // updateConfig
    const updateConfig = (key, value) => {
        setConfig(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // resetConfig
    const resetConfig = () => {
        setConfig(DEFAULT_CONFIG);
    };


    // Funciones helper para conceptos (optimizadas con useMemo)
    const getTaxConceptsByTipo = useMemo(() => {
        return (tipo) => {
            if (!tipo) return taxConcepts;
            return taxConcepts.filter(c =>
                c.tipo && c.tipo.toUpperCase() === tipo.toUpperCase()
            );
        };
    }, [taxConcepts]);

    // Opciones pre-calculadas para selects (memoizadas)
    const taxConceptsOptionsAll = useMemo(() => {
        return taxConcepts.map(concept => ({
            value: concept.idconceptos,
            label: `[${concept.idconceptos}] ${concept.descripcion_abreviada} ${concept.subtipo ? `${concept.subtipo}` : ''}`,
            code: concept.idconceptos,
            porcentaje: concept.porcentaje,
            tipo: concept.tipo
        }));
    }, [taxConcepts]);

    const taxConceptsOptionsISLR = useMemo(() => {
        return taxConcepts
            .filter(c => c.tipo && c.tipo.toUpperCase() === 'ISLR')
            .map(concept => ({
                value: concept.idconceptos,
                label: `[${concept.idconceptos}] ${concept.descripcion_abreviada} ${concept.subtipo ? `(${concept.subtipo})` : ''} - ${concept.porcentaje}`,
                code: concept.idconceptos,
                porcentaje: concept.porcentaje,
                tipo: concept.tipoo
            }));
    }, [taxConcepts]);

    const taxConceptsOptionsIVA = useMemo(() => {
        return taxConcepts
            .filter(c => c.tipo && c.tipo.toUpperCase() === 'IVA')
            .map(concept => ({
                value: concept.idconceptos,
                label: `[${concept.idconceptos}] ${concept.descripcion_abreviada} ${concept.subtipo ? `(${concept.subtipo})` : ''} - ${concept.porcentaje}`,
                code: concept.idconceptos,
                porcentaje: concept.porcentaje,
                tipo: concept.tipoo
            }));
    }, [taxConcepts]);

    const getTaxConceptsAsOptions = useMemo(() => {
        return (tipo = null) => {
            const filtered = tipo ? getTaxConceptsByTipo(tipo) : taxConcepts;
            return filtered.map(concept => ({
                value: concept.idconceptos,
                label: `[${concept.idconceptos}] ${concept.descripcion_abreviada} ${concept.subtipo ? `(${concept.subtipo})` : ''} - ${concept.porcentaje}`,
                code: concept.idconceptos,
                porcentaje: concept.porcentaje,
                tipo: concept.tipoo
            }));
        };
    }, [taxConcepts, getTaxConceptsByTipo]);

    const getTaxConceptById = useMemo(() => {
        return (id) => {
            return taxConcepts.find(c => c.idconceptos === String(id).padStart(3, '0'));
        };
    }, [taxConcepts]);

    // Opciones de tipos de documento para selects (memoizadas)
    const documentTypesOptions = useMemo(() => {
        return documentTypes.map(docType => ({
            value: docType.idtipo,
            label: docType.descripcion,
            id: docType.idtipo
        }));
    }, [documentTypes]);

    return (
        <AppConfigContext.Provider value={{
            config,
            updateConfig,
            resetConfig,
            taxConcepts,
            taxConceptsLoading,
            // Tipos de documento
            documentTypes,
            documentTypesLoading,
            documentTypesOptions,
            // Variables pre-calculadas (optimizadas)
            taxConceptsOptionsAll,
            taxConceptsOptionsISLR,
            taxConceptsOptionsIVA,
            // Funciones helper
            getTaxConceptsByTipo,
            getTaxConceptsAsOptions,
            getTaxConceptById
        }}>
            {children}
        </AppConfigContext.Provider>
    );
}
